import React from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Download, Loader2, PenLine, Type, XCircle } from 'lucide-react';
import SignaturePad from '../components/accounting/contracts/SignaturePad';

const API = process.env.REACT_APP_BACKEND_URL;
const err = (e, f) => e?.response?.data?.detail || f;

function SignForm({ doc, token, onDone }) {
  const [method, setMethod] = React.useState('typed');
  const [f, setF] = React.useState({ name: doc.prefill.name || '', designation: doc.prefill.designation || '', email: doc.prefill.email || '' });
  const [image, setImage] = React.useState(null);
  const [agree, setAgree] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [declining, setDeclining] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const verb = doc.kind === 'contract' ? 'Sign' : 'Accept';
  const ready = agree && f.name.trim().length > 1 && (method === 'typed' || image);

  const submit = async () => {
    setBusy(true); setError('');
    try { const r = await axios.post(`${API}/api/public/sign/${token}/accept`, { ...f, method, signature_image: image, agree }); onDone(r.data.status); }
    catch (e) { setError(err(e, 'Could not submit. Please try again.')); }
    finally { setBusy(false); }
  };
  const decline = async () => {
    setBusy(true);
    try { await axios.post(`${API}/api/public/sign/${token}/decline`, { reason }); onDone('declined'); }
    catch (e) { setError(err(e, 'Could not submit')); }
    finally { setBusy(false); }
  };
  const input = (k, label, type = 'text') => (
    <label className="block"><span className="text-xs font-semibold text-slate-600">{label}</span>
      <input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300" data-testid={`sign-${k}`} />
    </label>
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-4 shadow-sm" data-testid="sign-form">
      <h2 className="text-lg font-bold text-slate-900">{verb} {doc.kind === 'contract' ? 'this agreement' : 'this proposal'}</h2>
      <div className="grid sm:grid-cols-3 gap-3">{input('name', 'Full name *')}{input('designation', 'Designation')}{input('email', 'Email', 'email')}</div>
      <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100 max-w-xs">
        {[['typed', 'Type name', Type], ['drawn', 'Draw signature', PenLine]].map(([k, l, I]) => (
          <button key={k} type="button" onClick={() => setMethod(k)} data-testid={`sign-method-${k}`}
            className={`py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${method === k ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}><I className="w-3.5 h-3.5" />{l}</button>
        ))}
      </div>
      {method === 'typed' ? (
        <div className="h-20 rounded-xl border border-slate-200 bg-slate-50 flex items-center px-5 text-3xl text-slate-800" style={{ fontFamily: '"Brush Script MT", "Segoe Script", cursive' }} data-testid="sign-typed-preview">{f.name || 'Your name'}</div>
      ) : <SignaturePad onChange={setImage} />}
      <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 w-4 h-4 accent-fuchsia-600" data-testid="sign-agree" />
        I have read the {doc.kind === 'contract' ? 'agreement' : 'proposal'} above and agree to its terms on behalf of the client. I understand this is a legally binding electronic signature.
      </label>
      {error && <p className="text-sm text-rose-600" data-testid="sign-error">{error}</p>}
      <div className="flex flex-wrap gap-3 items-center">
        <button type="button" disabled={!ready || busy} onClick={submit} data-testid="sign-submit"
          className="px-6 h-11 rounded-full bg-gray-900 text-white text-sm font-semibold disabled:opacity-40 hover:bg-gray-800 transition-colors inline-flex items-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{verb} {doc.kind === 'contract' ? 'agreement' : 'proposal'}
        </button>
        {doc.kind === 'proposal' && !declining && <button type="button" onClick={() => setDeclining(true)} className="text-sm text-slate-500 hover:text-rose-600" data-testid="sign-decline-open">Decline</button>}
      </div>
      {declining && (
        <div className="space-y-2 pt-2 border-t border-slate-100" data-testid="sign-decline-box">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Anything you'd like us to know? (optional)" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="sign-decline-reason" />
          <button type="button" onClick={decline} disabled={busy} className="px-4 h-9 rounded-full border border-rose-200 text-rose-600 text-sm font-semibold hover:bg-rose-50" data-testid="sign-decline-submit">Decline proposal</button>
        </div>
      )}
    </div>
  );
}

export default function SignDocumentPage() {
  const { token } = useParams();
  const [doc, setDoc] = React.useState(null);
  const [error, setError] = React.useState('');
  const [done, setDone] = React.useState(null);
  const load = React.useCallback(() => axios.get(`${API}/api/public/sign/${token}`).then((r) => setDoc(r.data)).catch((e) => setError(err(e, 'This link is not valid'))), [token]);
  React.useEffect(() => { load(); }, [load]);
  const finish = (status) => { setDone(status); load(); };

  if (error) return <div className="min-h-screen flex items-center justify-center bg-[#f6f6f4] p-6"><div className="text-center" data-testid="sign-page-error"><XCircle className="w-10 h-10 text-rose-400 mx-auto mb-3" /><p className="text-slate-700 font-semibold">{error}</p></div></div>;
  if (!doc) return <div className="min-h-screen flex items-center justify-center bg-[#f6f6f4]"><Loader2 className="w-7 h-7 animate-spin text-fuchsia-600" /></div>;

  return (
    <div className="min-h-screen bg-[#f6f6f4]" data-testid="sign-page">
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="https://customer-assets.emergentagent.com/job_careers-hub-9/artifacts/6q64xjbl_HED%20NEW%20LOGO%20BLACK%20PNG.png" alt="Half Eaten Donut" className="h-7" />
          <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-900 truncate">{doc.title} · {doc.brand}</p><p className="text-[11px] text-slate-500">{doc.number}</p></div>
          <a href={`${API}/api/public/sign/${token}/pdf`} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="sign-download-pdf"><Download className="w-4 h-4" /> PDF</a>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {(doc.completed || done === 'declined' || doc.status === 'declined') && (
          <div className={`rounded-2xl p-4 flex gap-3 items-center ${doc.status === 'declined' ? 'bg-rose-50 text-rose-800' : 'bg-emerald-50 text-emerald-800'}`} data-testid="sign-status-banner">
            {doc.status === 'declined' ? <XCircle className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6" />}
            <p className="text-sm font-semibold">{doc.status === 'declined' ? 'This proposal was declined. Thank you for letting us know.' : `${doc.kind === 'contract' ? 'Signed' : 'Accepted'} by ${doc.signed?.name}. A signed copy will be emailed to you shortly, and you can download the PDF above at any time.`}</p>
          </div>
        )}
        {!doc.completed && !doc.can_sign && doc.status !== 'declined' && <div className="rounded-2xl p-4 bg-amber-50 text-amber-800 text-sm font-semibold" data-testid="sign-unavailable">This document can no longer be signed ({doc.status}). Please contact Half Eaten Donut.</div>}
        <iframe title="Document" srcDoc={doc.html} className="w-full h-[70vh] rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="sign-document-frame" />
        {doc.can_sign && !doc.completed && <SignForm doc={doc} token={token} onDone={finish} />}
      </main>
    </div>
  );
}
